/* ==========================================================================
   The Anchored — landing page behaviour

   Two behaviours only:
     1. Product galleries — click or hover a thumbnail to cross-fade that image
        in. State is independent per card.
     2. Sticky mobile buy bar — visible on phones once the hero CTA has
        scrolled out of view.

   Re-initialises on shopify:section:load so it works in the theme editor.
   ========================================================================== */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 767px)';
  var SCROLL_THRESHOLD = 340;

  function AnchoredLanding(root) {
    this.root = root;
    this.bar = root.querySelector('[data-anc-stickybar]');
    this.mq = window.matchMedia(MOBILE_QUERY);
    this.isMobile = this.mq.matches;
    this.scrolledPastHero = window.scrollY > SCROLL_THRESHOLD;

    this.onGalleryEvent = this.onGalleryEvent.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onMediaChange = this.onMediaChange.bind(this);

    // One delegated listener per card handles every thumbnail.
    this.galleries = Array.prototype.slice.call(
      root.querySelectorAll('[data-anc-gallery]')
    );

    this.galleries.forEach(
      function (gallery) {
        gallery.addEventListener('click', this.onGalleryEvent);
        gallery.addEventListener('mouseover', this.onGalleryEvent);
        gallery.addEventListener('focusin', this.onGalleryEvent);
      }.bind(this)
    );

    if (this.bar) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.mq.addEventListener) {
        this.mq.addEventListener('change', this.onMediaChange);
      } else {
        this.mq.addListener(this.onMediaChange); // Safari < 14
      }
      this.syncBar();
    }
  }

  AnchoredLanding.prototype.onGalleryEvent = function (event) {
    var thumb = event.target.closest('[data-anc-thumb]');
    if (!thumb) return;

    var gallery = thumb.closest('[data-anc-gallery]');
    if (!gallery) return;

    var index = thumb.getAttribute('data-anc-thumb');
    if (thumb.classList.contains('is-active')) return;

    gallery.querySelectorAll('[data-anc-thumb]').forEach(function (el) {
      var active = el === thumb;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    gallery.querySelectorAll('[data-anc-slide]').forEach(function (el) {
      el.classList.toggle('is-active', el.getAttribute('data-anc-slide') === index);
    });
  };

  // Only writes state on a threshold crossing, not on every frame.
  AnchoredLanding.prototype.onScroll = function () {
    var past = window.scrollY > SCROLL_THRESHOLD;
    if (past === this.scrolledPastHero) return;
    this.scrolledPastHero = past;
    this.syncBar();
  };

  AnchoredLanding.prototype.onMediaChange = function (event) {
    this.isMobile = event.matches;
    this.syncBar();
  };

  AnchoredLanding.prototype.syncBar = function () {
    if (!this.bar) return;
    this.bar.classList.toggle('is-visible', this.isMobile && this.scrolledPastHero);
  };

  AnchoredLanding.prototype.destroy = function () {
    this.galleries.forEach(
      function (gallery) {
        gallery.removeEventListener('click', this.onGalleryEvent);
        gallery.removeEventListener('mouseover', this.onGalleryEvent);
        gallery.removeEventListener('focusin', this.onGalleryEvent);
      }.bind(this)
    );

    window.removeEventListener('scroll', this.onScroll);

    if (this.mq.removeEventListener) {
      this.mq.removeEventListener('change', this.onMediaChange);
    } else {
      this.mq.removeListener(this.onMediaChange);
    }
  };

  var instances = new WeakMap();

  function mount(scope) {
    (scope || document).querySelectorAll('[data-anc-root]').forEach(function (root) {
      if (instances.has(root)) return;
      instances.set(root, new AnchoredLanding(root));
    });
  }

  function unmount(scope) {
    (scope || document).querySelectorAll('[data-anc-root]').forEach(function (root) {
      var instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(); });
  } else {
    mount();
  }

  // Theme editor: sections are torn down and re-rendered on every edit.
  document.addEventListener('shopify:section:load', function (event) {
    mount(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    unmount(event.target);
  });
})();
